<template>
  <div v-if="isServiceExist">
    <div>
      <v-row class="mb-6">
        <v-col cols="12">
          <gg-card-title class="pt-0">
            <hosting-breadcrumbs :managed-item-name="managedItemName" :hostingid="hostingid" />
            <div v-if="initialRoute">
              <div>
                {{ label }} {{ managedItemName }}
              </div>
            </div>
            <div v-else>
              {{ typeof $route.meta.pageSubtitle === 'function' ? $route.meta.pageSubtitle() : $route.meta.pageSubtitle }}
            </div>
          </gg-card-title>

          <template v-if="loading && showAdvancedLink">
            <gg-loading :loading="loading" height="10" max-width="200" type="image" />
          </template>
          <template v-else>
            <div class="body-2 pt-2" v-if="showAdvancedLink">
              Advanced users:
              <template>
                <a :href="cpanelLoginUrl" target="_blank">cPanel<v-icon small class='mb-1 ml-1'>mdi-open-in-new</v-icon></a>
              </template>
              <template v-if="this.isReseller">
                <span> / </span>
                <a :href="whmLoginUrl" target="_blank">WHM<v-icon small class='mb-1 ml-1'>mdi-open-in-new</v-icon></a>
              </template>
            </div>
          </template>
        </v-col>
        <v-col v-if="hideSelect && !isResellerSubAccount" cols="12">
          <hosting-switch />
        </v-col>
      </v-row>
      <component :is="service_type" :loading="loading" />
    </div>
  </div>
  <preloader v-else></preloader>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import SharedService from 'Components/Services/Hosting/Shared/SharedService'
import ResellerService from 'Components/Services/Hosting/Reseller/ResellerService'
import VpsService from 'Components/Services/Hosting/VPS/VpsService'
import DedicatedService from 'Components/Services/Hosting/Dedicated/DedicatedService'
import HostingSwitch from '@/components/Services/HostingSwitch'
import Preloader from '@/components/Common/Preloader'
import SubAccountSwitch from '@/components/Services/SubAccountSwitch'
import HostingBreadcrumbs from '@/components/Services/HostingBreadcrumbs.vue'

const routeNames = [
  'hosting_create_request',
  'hosting_view_request',
  'hosting_cpanel_info',
  'email-accounts',
  'hosting_email_forwarders',
  'hosting_email_autoresponders',
  'hosting_email_filters',
  'hosting_email_authentication',
  'hosting_spam_filters',
  'hosting_wp_manage',
  'hosting_wp_staging',
  'hosting_wp_autoupdate',
  'hosting_cpanel_ftp_accounts',
  'hosting_sitebuilder',
  'hosting_ssl',
  'protected_urls',
  'hosting_cpanel_blocked_ip',
  'hosting_cpanel_addon_domains',
  'hosting_parked_domains',
  'hosting_cpanel_subdomains',
  'hosting_cpanel_redirects',
  'hosting_cpanel_zone_editor',
  'hosting_cpanel_change_primary_domain',
  'hosting_cpanel_mysql',
  'hosting_mysql_users',
  'hosting_cpanel_mysql_remote',
  'hosting_cpanel_php_my_admin',
  'hosting_cpanel_error_log',
  'hosting_cpanel_cron_jobs',
  'hosting_cpanel_manage_ssh',
  'hosting_git',
  'hosting_addons',
  'hosting_whm_info',
  'hosting_whm_sitebuilder',
  'hosting_whm_password',
  'hosting_whm_addons',
  'hosting_whm_list_accounts',
  'hosting_whm_account_add',
  'hosting_whm_packages',
  'hosting_email_filters_manage',
  'hosting_ssl_manage',
  'hosting_ssl_create',
  'hosting_whm_zone_editor',
  'hosting_app_installer',
]

export default {
  name: 'ServiceSingleView',
  components: {
    SharedService,
    ResellerService,
    VpsService,
    DedicatedService,
    HostingSwitch,
    Preloader,
    SubAccountSwitch,
    HostingBreadcrumbs
  },
  props: {
    hostingid: {
      required: true,
      type: [String, Number],
    },
  },
  data: () => ({
    isServiceExist: false,
    loading: false,
    service: {},
    cpanelLoginUrl: '',
    whmLoginUrl: ''
  }),
  computed: {
    ...mapGetters([
      'hosting',
      'hostingPopulated',
      'routerAnimation',
      'resellerSubAccounts',
      'hosting_detail'
    ]),
    service_type () {
      if (this.$route.query.user && this.service.type === 'whm') {
        return 'shared-service'
      }

      switch (this.service.type) {
        case 'cpanel':
          return 'shared-service'
        case 'whm':
          return 'reseller-service'
        case 'vserver':
          return 'vps-service'
        case 'dserver':
          return 'dedicated-service'
        default:
          return ''
      }
    },
    initialRoute () {
      const initialRoutes = [
        'hosting_cpanel_info',
        'hosting_whm_info',
        'hosting_vserver_info',
        'hosting_dserver_info]'
      ]

      return !!initialRoutes.find(item => item === this.$route.name)
    },
    hideSelect () {
      return routeNames.indexOf(this.$route.name) === -1
    },
    isResellerSubAccount () {
      return !!(this.service.type === 'whm' && this.$route.query.user)
    },
    subAccountsList () {
      return this.resellerSubAccounts || []
    },
    currentSubAccount () {
      return this.subAccountsList.find((item) => {
        return item.user === this.$route.query.user
      })
    },
    managedItemName () {
      return this.isResellerSubAccount
        ? (this.currentSubAccount?.domain ?? (this.hosting_detail?.accountsummary?.domain || ''))
        : this.hosting.domain
    },
    label () {
      return this.$route.meta.parent === 'hosting_reseller' ? `Reseller Dashboard for` : 'Managing Website:'
    },
    showAdvancedLink () {
      return ['hosting_cpanel_info', 'hosting_whm_info'].includes(this.$route.name)
    },
    isReseller () {
      return this.service.type === 'whm'
    },
    isResellerRoute () {
      return this.$route.meta.parent === 'hosting_reseller'
    },
  },
  methods: {
    ...mapActions(['getHosting', 'getHostingDetail', 'getcpanelloginurl', 'getwhmloginurl', 'reportError']),
    async getHostingData () {
      try {
        this.loading = true
        this.isServiceExist = false

        const service = await this.getHosting(this.hostingid)
        this.service = service
        this.isServiceExist = true

        // Call getHostingDetail() only is service is active
        if (!this.inactiveService(service.status)) {
          await this.callHostingDetail()
        }

        if (!this.showAdvancedLink) {
          this.loading = false
          return
        }

        if (this.isReseller) {
          await this.getWhmUrl()
        }

        await this.getCpanelUrl()

        this.loading = false
      } catch (e) {
        if (e.message === 'UNK_SRV') {
          await this.$router.push({
            name: this.$route.meta.parent
          })
          this.reportError('Unknown Service')
          return
        }
        this.reportError(e)
      }
    },
    callHostingDetail (user = '') {
      // const isResellerRoute = this.$route.meta.parent === 'hosting_reseller'
      return this.getHostingDetail(user || this.$route.query.user)
    },
    inactiveService (status) {
      return status === 'Pending' || status === 'Suspended'
    },
    async getCpanelUrl () {
      this.cpanelLoginUrl = await this.getcpanelloginurl({ id: this.hostingid })
    },
    async getWhmUrl () {
      this.whmLoginUrl = await this.getwhmloginurl(this.hostingid)// Future implementation for WHM URL retrieval
    }
  },
  watch: {
    async hostingid () {
      await this.getHostingData()
    },
    async '$route.query.user' (current, previous) {
      // reload hosting data and details when user query param changes
      await this.getHostingData()
      this.callHostingDetail(current)
    }
  },
  async created () {
    await this.getHostingData()
    if (this.isResellerSubAccount) {
      await this.callHostingDetail()
      // The next 2 lines used for correct page <title> for reseller's subaccount after reloading the page
      const domain = document.title.split(' - ', 1)[0]
      document.title = document.title.replace(domain, this.managedItemName)
    }
  },
}
</script>

<style lang="scss">
  .no-bg {
    background-color: transparent;
  }
</style>
